import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import React from 'react';
import {
    AirplaneOutline,
    ChevronDownOutline,
    ChevronUpOutline,
    CloseOutline,
    FilterOutline,
    FilterSolid,
    PinOutline,
    TrashOutline
} from '../../../../icons/icons';
import { useWorkspaceItemContext } from '../../../contexts/workspace/workspace-item-context';
import { useAppDispatch, useAppSelector } from '../../../hooks/store-hooks';
import { followFlight, removeAllPinnedFlights, removePinnedFlight, togglePinnedVisibility } from '../../../state/slices/workspace-slice';
import { useFocusedWorkspaceSelector, useWorkspaceView } from '../hooks/use-view';
import FlightHeader from './FlightHeader';

export default function PinnedFlights() {
    const dispatch = useAppDispatch();
    const context = useWorkspaceItemContext();
    const pinnedFlights = useAppSelector((x) => x.workspace.flights.pinned);
    const alwaysShowPinned = useAppSelector((x) => x.workspace.flights.settings.showPinned);
    const followedFlight = useFocusedWorkspaceSelector((x) => x.flights.followed);
    const [expanded, setExpanded] = React.useState(true);
    const hasFilter = useWorkspaceView((x) => x.plane.filter.useFilters);

    const removePinned = React.useCallback(
        (id: number) => {
            dispatch(removePinnedFlight({ workspaceId: context.id, flightId: id }));
        },
        [context]
    );

    const handleFollowMe = (id: number) => {
        if (followedFlight === id) {
            dispatch(followFlight(null));
        } else {
            dispatch(followFlight(id));
        }
    };

    if (pinnedFlights.length === 0) {
        return <></>;
    }

    return (
        <div className="pe-auto max-h-100 d-flex k-card flex-column gap-0" style={{ width: expanded ? 367 : 200 }}>
            <div
                className="d-flex align-items-center px-2 py-1 gap-1 cursor-pointer secondary-bg-color"
                onClick={() => setExpanded(!expanded)}>
                <SvgIcon icon={PinOutline} size="small" />
                <div className="flex-grow-1">Pinned ({pinnedFlights.length})</div>
                {hasFilter && (
                    <Button
                        title={alwaysShowPinned ? 'Hide pinned flights if filtered out' : 'Show pinned flights despite filters'}
                        className="button-small"
                        fillMode={'flat'}
                        svgIcon={alwaysShowPinned ? FilterSolid : FilterOutline}
                        onClick={(e) => {
                            e.stopPropagation();
                            dispatch(togglePinnedVisibility());
                        }}
                    />
                )}

                <Button
                    title="Remove all pinned"
                    className="button-small"
                    fillMode={'flat'}
                    svgIcon={TrashOutline}
                    onClick={(e) => {
                        e.stopPropagation();
                        dispatch(removeAllPinnedFlights());
                    }}
                />
                <Button className="button-small" fillMode={'flat'} svgIcon={expanded ? ChevronUpOutline : ChevronDownOutline} />
            </div>
            {expanded && (
                <div className="d-flex h-100 min-h-0 flex-column border-top overflow-auto">
                    {pinnedFlights.map((x) => (
                        <div
                            key={x.id}
                            className="pe-2 d-flex flex-row border-bottom clickable"
                            onClick={() => context.flightLayerFacade.selection.selectFlight(x.id, { source: 'pinned' })}>
                            <div className="flex-grow-1">
                                <FlightHeader flight={x} airportSummaryLinks={false} />
                            </div>
                            <div className="d-flex flex-column gap-1 justify-content-center">
                                <Button
                                    title="Unpin"
                                    fillMode={'flat'}
                                    svgIcon={CloseOutline}
                                    className="button-small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removePinned(x.id);
                                    }}
                                />
                                <Button
                                    title="Follow me"
                                    fillMode={followedFlight === x.id ? 'solid' : 'flat'}
                                    svgIcon={AirplaneOutline}
                                    className="button-small"
                                    themeColor={followedFlight === x.id ? 'primary' : undefined}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleFollowMe(x.id);
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
